import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Upload, FileText, AlertCircle, CheckCircle2 } from "lucide-react";
import type { Recommendation, CompletionMethod } from "@/types/artie-score";
import { apiClient } from "@/lib/api";

interface CompleteRecommendationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recommendation: Recommendation;
  projectId: string;
  onComplete: () => void;
}

export const CompleteRecommendationDialog = ({
  open,
  onOpenChange,
  recommendation,
  projectId,
  onComplete,
}: CompleteRecommendationDialogProps) => {
  const [completionMethod, setCompletionMethod] = useState<CompletionMethod>("plan");
  const [planDescription, setPlanDescription] = useState("");
  const [artifactFiles, setArtifactFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setArtifactFiles(Array.from(e.target.files));
    }
  };

  const handleComplete = async () => {
    setError(null);
    setIsLoading(true);

    try {
      // Validate inputs
      if (completionMethod === "plan" && !planDescription.trim()) {
        setError("Please describe your plan");
        setIsLoading(false);
        return;
      }

      if (completionMethod === "artifact" && artifactFiles.length === 0) {
        setError("Please upload at least one artifact");
        setIsLoading(false);
        return;
      }

      let artifactUrls: string[] = [];

      // Upload artifacts if method is artifact
      if (completionMethod === "artifact" && artifactFiles.length > 0) {
        // TODO: Implement actual file upload to S3
        // For now, we'll use placeholder URLs
        artifactUrls = artifactFiles.map((file, idx) => 
          `https://placeholder-s3-url.com/${projectId}/${recommendation.id}/${file.name}`
        );
        
        // In production, you would:
        // 1. Get presigned URLs from backend
        // 2. Upload files to S3
        // 3. Get final URLs
        // Example:
        // for (const file of artifactFiles) {
        //   const initRes = await apiClient.initArtifactUpload(projectId, recommendation.id, file.name);
        //   await uploadToS3(initRes.upload_url, file, initRes.fields);
        //   artifactUrls.push(initRes.final_url);
        // }
      }

      // Complete the recommendation
      await apiClient.completeRecommendation(
        projectId,
        recommendation.id || recommendation.what_to_improve,
        completionMethod,
        completionMethod === "artifact" ? artifactUrls : undefined,
        completionMethod === "plan" ? planDescription : undefined
      );

      onComplete();
      onOpenChange(false);
      
      // Reset form
      setPlanDescription("");
      setArtifactFiles([]);
      setCompletionMethod("plan");
    } catch (err: any) {
      setError(err.message || "Failed to complete recommendation");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            Mark as Complete
          </DialogTitle>
          <DialogDescription>
            {recommendation.what_to_improve}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Completion Method Selection */}
          <div>
            <Label className="text-base font-semibold mb-3 block">
              How did you complete this recommendation?
            </Label>
            <RadioGroup
              value={completionMethod}
              onValueChange={(value) => setCompletionMethod(value as CompletionMethod)}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3 p-4 border-2 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer">
                <RadioGroupItem value="artifact" id="artifact" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="artifact" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-1">
                      <Upload className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold">Upload Artifact</span>
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                        Higher Score Impact
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Upload files that prove the work is done or in motion (contracts, screenshots, documents, etc.)
                    </p>
                  </Label>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-4 border-2 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer">
                <RadioGroupItem value="plan" id="plan" className="mt-1" />
                <div className="flex-1">
                  <Label htmlFor="plan" className="cursor-pointer">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-purple-600" />
                      <span className="font-semibold">Describe Plan</span>
                      <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                        Lower Score Impact
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Describe your plan or approach to address this recommendation
                    </p>
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Artifact Upload */}
          {completionMethod === "artifact" && (
            <div className="space-y-3">
              <Label htmlFor="artifact-upload" className="text-sm font-medium">
                Upload Supporting Materials
              </Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  id="artifact-upload"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Label htmlFor="artifact-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm font-medium">Click to upload files</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PDF, images, documents, etc.
                  </p>
                </Label>
              </div>
              {artifactFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Selected files:</p>
                  {artifactFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm bg-accent p-2 rounded">
                      <FileText className="w-4 h-4" />
                      <span className="flex-1">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Plan Description */}
          {completionMethod === "plan" && (
            <div className="space-y-3">
              <Label htmlFor="plan-description" className="text-sm font-medium">
                Describe Your Plan
              </Label>
              <Textarea
                id="plan-description"
                value={planDescription}
                onChange={(e) => setPlanDescription(e.target.value)}
                placeholder="Describe what you've done or plan to do to address this recommendation..."
                className="min-h-[120px] resize-none"
              />
              <p className="text-xs text-muted-foreground">
                Be specific about your approach, timeline, and expected outcomes
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Score Impact</p>
              <p>
                {completionMethod === "artifact" 
                  ? "Uploading artifacts provides stronger evidence of completion and will have a higher positive impact on your score when you recalculate."
                  : "Describing your plan still counts toward completion but will have a lower score impact compared to uploading artifacts."}
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleComplete}
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Completing...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark as Complete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};